import { useFormContext, useWatch } from "react-hook-form";
import { ClientAccountTypeEnum } from "@/__generated__/graphql";
import { Divider } from "@/components/shared/divider";
import { LuceSparkle, ShoppingCart } from "@/components/shared/icons";
import { ListHeading } from "@/components/shared/list-header";
import { Typography } from "@/components/shared/typography";
import { Button, Card, CardContent, View } from "@/components/ui";
import { formatPrice } from "@/lib/helpers/number";
import { useAuthState } from "@/store/auth";

import type { GiftCardFormData } from "./use-gift-card-form";
import { useSubmitGiftCard } from "./use-submit-gift-card";

export function SectionGiftCardOrder() {
  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useFormContext<GiftCardFormData>();

  const { onSubmit: submitGiftCard } = useSubmitGiftCard();

  const accountType = useAuthState((state) => state.data.userInfo.accountType);
  const isCorporate = accountType === ClientAccountTypeEnum.Corporate;

  const [amount, quantity] = useWatch({ control, name: ["amount", "quantity"] });
  const unitAmount = Number(amount);
  const itemCount = isCorporate ? Math.max(Number(quantity) || 0, 0) : 1;
  const totalAmount = unitAmount * itemCount;
  const hasItems = unitAmount > 0 && itemCount > 0;

  const onSubmit = handleSubmit(submitGiftCard);

  return (
    <View>
      <Card className="sticky top-20 w-[304px]">
        <CardContent className="native:pt-0 p-4">
          <ListHeading title="Your Order" icon={ShoppingCart} />
          <Divider className="my-4" />
          {!hasItems ? (
            <View className="flex flex-col items-center justify-center gap-2 py-8">
              <ShoppingCart size={40} className="text-foreground-attenuated" weight="fill" />
              <Typography color="foreground-intermediate" variant="body-sm">
                No items selected
              </Typography>
            </View>
          ) : (
            <View className="flex flex-col gap-3 pb-3">
              <Typography variant="label-sm" color="foreground-default">
                Items
              </Typography>
              <View className="flex flex-row items-center justify-between">
                <View className="flex flex-row items-center gap-2">
                  <LuceSparkle className="text-brand-primary size-4" />
                  <Typography variant="body-sm" color="foreground-default">
                    Gift Card
                  </Typography>
                </View>
                <Typography variant="body-sm" color="foreground-default">
                  {`${itemCount} Item ($${unitAmount})`}
                </Typography>
              </View>
              <Divider />
              <Typography variant="label-sm" color="foreground-default">
                Payment Summary
              </Typography>
              <View className="flex flex-row items-center justify-between">
                <Typography variant="body-sm" color="foreground-intermediate">
                  Subtotal
                </Typography>
                <Typography variant="body-sm" color="foreground-default">
                  {`$${totalAmount}`}
                </Typography>
              </View>
              <Divider />
              <View className="flex flex-row items-center justify-between">
                <Typography variant="label-sm" color="foreground-default">
                  {`Total (${itemCount} Item)`}
                </Typography>
                <Typography variant="label-sm" color="brand-primary">
                  {formatPrice(totalAmount)}
                </Typography>
              </View>
            </View>
          )}
          <Button
            variant="primary"
            color="CTA"
            loading={isSubmitting}
            fullWidth="full"
            iconAlignment="end"
            iconName="arrowRight"
            onClick={onSubmit}
          >
            Continue
          </Button>
          <Typography
            variant="caption-md"
            color="foreground-intermediate"
            align="center"
            className="mt-3"
          >
            Clicking “Continue” will automatically create an invoice and take you to the payment
            page.
          </Typography>
        </CardContent>
      </Card>
    </View>
  );
}
